import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { Separator } from '@/design/components/separator';
import { cn } from '@/design/utils';
import type { FieldConflict, FieldMap, Resolution } from '@/features/conflicts/conflict';
import { applyResolutions, fullyResolved } from '@/features/conflicts/conflict';

/**
 * The field-level conflict dialog: what diverged, both values, and a decision per field.
 *
 * **There is no default and no "save mine anyway".** The save button stays disabled until every
 * divergence has been answered, and the message says so. That is the whole requirement: a 409 must
 * never turn into a silent overwrite, because the other person's change is invisible from this
 * screen and the records are people's identity documents.
 *
 * **Reloading is offered as the other way out**, plainly labelled as discarding this operator's
 * changes, because for a small edit that is often what somebody actually wants and pretending
 * otherwise makes them resolve five rows to get there.
 *
 * **A field's label is supplied by the caller**, because only the caller knows whether `fields.2` is
 * a passport number or a telephone. The dialog renders what it is given and never invents a name
 * from a path.
 */
export function ConflictDialog({
  open,
  conflicts,
  theirs,
  mine,
  labelFor,
  onReload,
  onApply,
  onOpenChange,
}: Readonly<{
  open: boolean;
  conflicts: readonly FieldConflict[];
  theirs: FieldMap;
  mine: FieldMap;
  /** The human name of a field path, in the active language. */
  labelFor: (path: string) => string;
  onReload: () => void;
  onApply: (merged: FieldMap) => void;
  onOpenChange: (next: boolean) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<Record<string, Resolution | undefined>>({});

  const complete = fullyResolved(conflicts, choices);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('conflict.title')}</DialogTitle>
          <DialogDescription>{t('conflict.body')}</DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-4" data-testid="conflict-rows">
          {conflicts.map((conflict) => (
            // The path is on the row so a test can answer one field rather than guessing from the
            // order the rows happen to be sorted in, which is what makes a per-field assertion
            // about the merge possible at all.
            <li
              key={conflict.path}
              data-conflict-path={conflict.path}
              className="flex flex-col gap-2"
            >
              <span className="text-sm font-medium">{labelFor(conflict.path)}</span>
              <div className="grid gap-2 sm:grid-cols-2">
                <ConflictChoice
                  selected={choices[conflict.path] === 'mine'}
                  heading={t('conflict.columnMine')}
                  value={conflict.mine}
                  emptyLabel={t('conflict.empty')}
                  actionLabel={t('conflict.keepMine')}
                  onSelect={() => {
                    setChoices((current) => ({ ...current, [conflict.path]: 'mine' }));
                  }}
                />
                <ConflictChoice
                  selected={choices[conflict.path] === 'theirs'}
                  heading={t('conflict.columnTheirs')}
                  value={conflict.theirs}
                  emptyLabel={t('conflict.empty')}
                  actionLabel={t('conflict.keepTheirs')}
                  onSelect={() => {
                    setChoices((current) => ({ ...current, [conflict.path]: 'theirs' }));
                  }}
                />
              </div>
              <Separator />
            </li>
          ))}
        </ul>

        {!complete && <p className="text-xs text-muted-foreground">{t('conflict.unresolved')}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onReload}>
            {t('conflict.reload')}
          </Button>
          <Button
            disabled={!complete}
            onClick={() => {
              onApply(applyResolutions(theirs, mine, choices));
            }}
          >
            {t('conflict.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConflictChoice({
  selected,
  heading,
  value,
  emptyLabel,
  actionLabel,
  onSelect,
}: Readonly<{
  selected: boolean;
  heading: string;
  value: string | null;
  emptyLabel: string;
  actionLabel: string;
  onSelect: () => void;
}>): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors',
        selected ? 'border-accent bg-accent-muted' : 'hover:bg-hover-surface',
      )}
    >
      <span className="text-2xs tracking-wide text-muted-foreground uppercase">{heading}</span>
      <span className={cn('text-sm', value === null && 'text-muted-foreground italic')}>
        {value ?? emptyLabel}
      </span>
      <span className="text-xs text-accent">{actionLabel}</span>
    </button>
  );
}
